"use client";

import { useFormatter, useLocale } from "next-intl";

import {
  DEFAULT_I18N_CONFIG,
  LOCALE_COOKIE_NAME,
  LOCALE_COOKIE_MAX_AGE,
} from "./config";

// ============================================================================
// Locale Access
// ============================================================================

/**
 * Get current locale in client component (hook)
 * This is the recommended way to get locale in React components
 *
 * @example
 * const locale = useCurrentLocale(); // "en" or "es"
 */
export function useCurrentLocale() {
  return useLocale();
}

/**
 * Get locale from cookie (non-hook utility)
 * @deprecated Since SGI-2744: NEXT_LOCALE cookie is now HttpOnly for security.
 * Use getCachedAxiosLocale() from @/libs/axios-cms-client instead.
 * This function will not work after HttpOnly is enabled.
 *
 * @example
 * // Old (deprecated):
 * const locale = getLocaleFromCookie();
 *
 * // New (use this):
 * import { getCachedAxiosLocale } from '@/libs/axios-cms-client';
 * const locale = getCachedAxiosLocale();
 */
export function getLocaleFromCookie(): string {
  if (globalThis.window === undefined) return DEFAULT_I18N_CONFIG.defaultLocale;

  const cookies = globalThis.document.cookie.split(";");
  const localeCookie = cookies.find((c) =>
    c.trim().startsWith(`${LOCALE_COOKIE_NAME}=`)
  );

  if (!localeCookie) return DEFAULT_I18N_CONFIG.defaultLocale;

  const locale = localeCookie.split("=")[1]?.trim();
  return locale || DEFAULT_I18N_CONFIG.defaultLocale;
}

/**
 * Set locale in cookie (client-side)
 * @deprecated Since SGI-2744: NEXT_LOCALE cookie is now HttpOnly and managed server-side.
 * Client-side writes are no longer supported. Locale changes should be handled via middleware.
 */
export function setLocaleInCookie(locale: string): void {
  if (globalThis.window === undefined) return;

  const isSecure = globalThis.window.location.protocol === "https:";

  globalThis.document.cookie = `${LOCALE_COOKIE_NAME}=${locale}; path=/; max-age=${LOCALE_COOKIE_MAX_AGE}; SameSite=Lax${
    isSecure ? "; Secure" : ""
  }`;
}

/**
 * Get Accept-Language header value from cached locale and wizard config
 * Used for API requests that need the backend locale code
 *
 * @deprecated Since SGI-2744: Use the cached locale from axios instance instead.
 * The axios interceptor now handles Accept-Language header automatically.
 * If you need the backend locale code directly, use getCachedBackendLocaleCode() from @/libs/axios
 *
 * @example
 * // Old (deprecated):
 * const acceptLanguage = await getAcceptLanguageHeader();
 *
 * // New (use this):
 * import { getCachedBackendLocaleCode } from '@/libs/axios';
 * const acceptLanguage = getCachedBackendLocaleCode(); // "es-ES"
 */
export async function getAcceptLanguageHeader(): Promise<string> {
  // Import dynamically to avoid circular dependency
  const { getCachedBackendLocaleCode } = await import("@/libs/axios");
  return getCachedBackendLocaleCode();
}

// ============================================================================
// Client-side Formatting Functions
// ============================================================================

/**
 * Client-side currency formatter
 * Use this in Client Components
 *
 * @example
 * function MyComponent() {
 *   const formatCurrency = useFormatCurrency();
 *   return <span>{formatCurrency(99.99)}</span>; // £99.99
 * }
 */
export function useFormatCurrency(): (amount: number) => string {
  const format = useFormatter();
  return (amount: number) => format.number(amount, "currency");
}

/**
 * Client-side date formatter
 * Use this in Client Components
 *
 * @example
 * function MyComponent() {
 *   const formatDate = useFormatDate();
 *   return <span>{formatDate(new Date(), 'long')}</span>;
 * }
 */
export function useFormatDate(): (
  date: Date | string,
  style?: "short" | "medium" | "long" | "full"
) => string {
  const format = useFormatter();
  return (
    date: Date | string,
    style: "short" | "medium" | "long" | "full" = "short"
  ) => format.dateTime(new Date(date), style);
}

/**
 * Client-side number formatter
 * Use this in Client Components
 *
 * @example
 * function MyComponent() {
 *   const formatNumber = useFormatNumber();
 *   return <span>{formatNumber(1234.56, 'precise')}</span>;
 * }
 */
export function useFormatNumber(): (
  value: number,
  style?: "precise"
) => string {
  const format = useFormatter();
  return (value: number, style?: "precise") => format.number(value, style);
}

/**
 * Client-side percent formatter
 * Use this in Client Components
 *
 * @example
 * function MyComponent() {
 *   const formatPercent = useFormatPercent();
 *   return <span>{formatPercent(0.75)}</span>; // 75%
 * }
 */
export function useFormatPercent(): (value: number) => string {
  const format = useFormatter();
  return (value: number) => format.number(value, { style: "percent" });
}

/**
 * Client-side relative time formatter
 * Use this in Client Components
 *
 * @example
 * function MyComponent() {
 *   const formatRelativeTime = useFormatRelativeTime();
 *   return <span>{formatRelativeTime(new Date())}</span>; // "2 days ago"
 * }
 */
export function useFormatRelativeTime(): (date: Date | string) => string {
  const format = useFormatter();
  return (date: Date | string) => format.relativeTime(new Date(date));
}
